import React from 'react';

/**
 * DashboardCard - A premium glassmorphic container for dashboard widgets
 *
 * Props:
 * @param {string} title - The title of the card
 * @param {React.ReactNode} children - The content of the card
 * @param {React.ReactNode} actions - Optional action buttons (top right)
 * @param {boolean} fullScreen - Whether the card is in full-screen mode
 * @param {string} gradient - Optional override for the border gradient ('primary', 'secondary', 'success', etc.)
 * @param {string} delay - Animation delay (e.g., '0.1s')
 */
const DashboardCard = ({
    title,
    children,
    actions,
    fullScreen = false,
    gradient = 'primary',
    delay = '0s'
}) => {
    const borderColor = gradient === 'secondary' ? '#3b82f6' :
        gradient === 'success' ? '#10b981' :
            gradient === 'danger' ? '#ef4444' :
                '#ff6b35';

    return (
        <div
            style={{
                position: 'relative',
                background: fullScreen
                    ? 'transparent'
                    : 'linear-gradient(145deg, rgba(20, 26, 44, 1) 0%, rgba(15, 20, 35, 1) 100%)',
                backdropFilter: fullScreen ? 'none' : 'blur(0px)',
                borderRadius: fullScreen ? '0' : '24px',
                border: fullScreen ? 'none' : `1px solid ${borderColor}55`,
                boxShadow: fullScreen
                    ? 'none'
                    : `0 12px 40px rgba(0, 0, 0, 0.8), 0 0 0 1px ${borderColor}33 inset, 0 2px 8px rgba(0, 0, 0, 0.5) inset`,
                padding: fullScreen ? '0' : '24px',
                display: 'flex',
                flexDirection: 'column',
                height: fullScreen ? 'auto' : '100%',
                minHeight: fullScreen ? '100%' : '100%',
                transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
                animation: fullScreen ? 'none' : `fadeInUp 0.6s cubic-bezier(0.2, 0.8, 0.2, 1) ${delay} both`,
                overflow: fullScreen ? 'visible' : 'hidden',
                width: '100%',
            }}
            className="dashboard-card"
        >
            {/* Premium top border accent */}
            {!fullScreen && (
                <div
                    style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        height: '2px',
                        background: `linear-gradient(90deg, transparent, ${borderColor}, transparent)`,
                        opacity: 0.8,
                    }}
                />
            )}

            {/* Header Section */}
            {(title || actions) && (
                <div
                    style={{
                        display: 'flex',
                        justifyContent: fullScreen ? 'center' : 'space-between',
                        alignItems: 'center',
                        marginBottom: fullScreen ? '24px' : '20px',
                        position: 'relative',
                        zIndex: 10,
                        width: '100%',
                    }}
                >
                    {title && (
                        <h2
                            style={{
                                margin: 0,
                                fontSize: fullScreen ? '44px' : '28px',
                                fontWeight: '700',
                                background: `linear-gradient(135deg, #ffffff 0%, #cbd5e1 100%)`,
                                WebkitBackgroundClip: 'text',
                                WebkitTextFillColor: 'transparent',
                                letterSpacing: '-0.02em',
                                textShadow: '0 2px 10px rgba(0,0,0,0.1)',
                                textAlign: fullScreen ? 'center' : 'left',
                                flex: 1,
                            }}
                        >
                            {title}
                        </h2>
                    )}

                    {actions && (
                        <div style={{
                            display: 'flex',
                            gap: '8px',
                            alignItems: 'center',
                            position: fullScreen ? 'absolute' : 'relative',
                            right: fullScreen ? 0 : 'auto',
                            top: fullScreen ? '50%' : 'auto',
                            transform: fullScreen ? 'translateY(-50%)' : 'none'
                        }}>
                            {actions}
                        </div>
                    )}
                </div>
            )}

            {/* Content Area */}
            <div style={{ flex: 1, minHeight: 0, position: 'relative', zIndex: 1 }}>
                {children}
            </div>

            {/* Subtle background glow effect */}
            {!fullScreen && (
                <div
                    style={{
                        position: 'absolute',
                        bottom: '-50px',
                        right: '-50px',
                        width: '200px',
                        height: '200px',
                        background: `radial-gradient(circle, ${borderColor}22 0%, transparent 70%)`,
                        pointerEvents: 'none',
                        zIndex: 0
                    }}
                />
            )}
        </div>
    );
};

export default DashboardCard;
